import { findMatchingRoute, migrateCardPolicyKeys } from './routeRegistry';
import {
  SIDEBAR_NAV_ITEMS,
  NAV_POLICY_PATH_ALIASES,
  getSidebarKeyForPath,
} from '../config/navPolicyCatalog';

export const NAV_POLICY_SETTING_KEY = 'NavPolicyConfig';
export const NAV_POLICY_SETTING_GROUP = 'Navigation';
export const NAV_POLICY_SETTING_DESCRIPTION =
  'Nav policy configuration for sidebar, cards, and pages';

export const EMPTY_NAV_POLICY_CONFIG = { sidebar: {}, cards: {}, pages: {} };

const SIDEBAR_KEYS = new Set(SIDEBAR_NAV_ITEMS.map(({ key }) => key));

let cachedConfig = null;
let loadPromise = null;
const listeners = new Set();

const notifyNavPolicyConfigListeners = (config) => {
  listeners.forEach((listener) => listener(config));
};

export const subscribeNavPolicyConfig = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Normalize persisted config — supports legacy flat sidebar keys at root.
 */
export const normalizeNavPolicyConfig = (raw = {}) => {
  if (!raw || typeof raw !== 'object') {
    return { ...EMPTY_NAV_POLICY_CONFIG };
  }

  const hasSections = raw.sidebar || raw.cards || raw.pages;
  if (hasSections) {
    const normalized = {
      sidebar: { ...(raw.sidebar || {}) },
      cards: migrateCardPolicyKeys(raw.cards || {}),
      pages: { ...(raw.pages || {}) },
    };
    if (normalized.sidebar.forms && !normalized.cards['/settings/forms']) {
      normalized.cards['/settings/forms'] = normalized.sidebar.forms;
      delete normalized.sidebar.forms;
    }
    return normalized;
  }

  const sidebar = {};
  const cards = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (key === 'forms') {
      cards['/settings/forms'] = value;
    } else if (SIDEBAR_KEYS.has(key)) {
      sidebar[key] = value;
    }
  });

  return {
    sidebar,
    cards: migrateCardPolicyKeys(cards),
    pages: {},
  };
};

export const getSectionPolicies = (config, section, key) => {
  const normalized = normalizeNavPolicyConfig(config);
  const policies = normalized[section]?.[key];
  if (!policies || (Array.isArray(policies) && policies.length === 0)) return null;
  return Array.isArray(policies) ? policies : [policies];
};

export const getSidebarPolicies = (config, key) =>
  getSectionPolicies(config, 'sidebar', key);

export const getCardPolicies = (config, key) =>
  getSectionPolicies(config, 'cards', key);

const resolvePagePolicyPaths = (pathname) => {
  const paths = [pathname];
  const alias = NAV_POLICY_PATH_ALIASES[pathname];
  if (alias) paths.push(alias);
  return paths;
};

export const getPagePoliciesForPath = (config, pathname) => {
  const normalized = normalizeNavPolicyConfig(config);

  for (const path of resolvePagePolicyPaths(pathname)) {
    const pagePolicies = getSectionPolicies(normalized, 'pages', path);
    if (pagePolicies) return pagePolicies;
  }

  const matchedRoute = findMatchingRoute(pathname);
  if (matchedRoute) {
    const patternPolicies = getSectionPolicies(normalized, 'pages', matchedRoute.path);
    if (patternPolicies) return patternPolicies;
  }

  const sidebarKey = getSidebarKeyForPath(pathname);
  if (sidebarKey) {
    const sidebarPolicies = getSidebarPolicies(normalized, sidebarKey);
    if (sidebarPolicies) return sidebarPolicies;
  }

  return null;
};

export const setCachedNavPolicyConfig = (config) => {
  cachedConfig = normalizeNavPolicyConfig(config);
  return cachedConfig;
};

export const publishNavPolicyConfig = (config) => {
  const normalized = setCachedNavPolicyConfig(config);
  notifyNavPolicyConfigListeners(normalized);
  return normalized;
};

export const clearNavPolicyConfigCache = () => {
  cachedConfig = null;
  loadPromise = null;
};

export const loadNavPolicyConfig = async () => {
  if (cachedConfig) return cachedConfig;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
      if (!userInfo?.token) return setCachedNavPolicyConfig({});

      const res = await fetch('/dotnet_api/v1/auth/nav-policy-config', {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      if (!res.ok) return setCachedNavPolicyConfig({});

      const data = await res.json();
      return setCachedNavPolicyConfig(JSON.parse(data.settingValue || '{}'));
    } catch {
      return setCachedNavPolicyConfig({});
    } finally {
      loadPromise = null;
    }
  })();

  return loadPromise;
};

export const refreshNavPolicyConfig = async () => {
  clearNavPolicyConfigCache();
  const config = await loadNavPolicyConfig();
  notifyNavPolicyConfigListeners(config);
  return config;
};

const sanitizePolicyList = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.filter((policy) => typeof policy === 'string' && policy.trim());
};

const sanitizeSection = (section = {}) =>
  Object.fromEntries(
    Object.entries(section).map(([key, policies]) => [key, sanitizePolicyList(policies)]),
  );

export const buildNavPolicyExportPayload = (config) => ({
  version: 1,
  key: NAV_POLICY_SETTING_KEY,
  exportedAt: new Date().toISOString(),
  config: normalizeNavPolicyConfig(config),
});

export const extractNavPolicyImportConfig = (data) => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid nav policy import file');
  }

  const raw = data.config ?? data.NavPolicyConfig ?? data;
  const normalized = normalizeNavPolicyConfig(raw);

  return {
    sidebar: sanitizeSection(normalized.sidebar),
    cards: sanitizeSection(normalized.cards),
    pages: sanitizeSection(normalized.pages),
  };
};

export const mergeNavPolicyConfig = (current, imported) =>
  normalizeNavPolicyConfig({
    sidebar: { ...normalizeNavPolicyConfig(current).sidebar, ...imported.sidebar },
    cards: { ...normalizeNavPolicyConfig(current).cards, ...imported.cards },
    pages: { ...normalizeNavPolicyConfig(current).pages, ...imported.pages },
  });

export const applyNavPolicyImport = (current, data, mode = 'Merge') => {
  const imported = extractNavPolicyImportConfig(data);
  if (mode === 'Override') {
    return imported;
  }
  return mergeNavPolicyConfig(current, imported);
};

export const saveNavPolicyConfig = async (config) => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
  if (!userInfo?.token) throw new Error('Not authenticated');

  const res = await fetch('/dotnet_api/v1/auth/nav-policy-config', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userInfo.token}`,
    },
    body: JSON.stringify({ configJson: JSON.stringify(normalizeNavPolicyConfig(config)) }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to save nav policy config');
  }
  return res.json();
};
